import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bookingPolicyApi,
  bookingPolicyQueryKeys,
  DIMENSION_OPTIONS,
  FILL_OPTIONS,
  QUEUE_RESET_OPTIONS,
  QUEUE_SCOPE_OPTIONS,
  SERVICE_SCOPE_OPTIONS,
  dimensionLabel,
  type BookingPolicy,
  type BookingDimensionKey,
  type DimensionMode,
  type FillMode,
  type ServiceScope,
} from "../../shared/api/bookingPolicy";
import { servicesApi, servicesQueryKeys } from "../../shared/api/services";
import { DEFAULT_DIMENSION_ORDER } from "../../shared/hooks/useBookingDimensions";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Chip trong bảng xem trước luồng đặt lịch. */
type PreviewChip = {
  label: string;
  tone: "required" | "optional" | "neutral" | "auto";
  note?: string;
};

/**
 * Tính danh sách bước đặt lịch mà bệnh nhân sẽ thấy, dựa trên giá trị form
 * hiện tại. Ẩn (HIDDEN) → không hiện chip; Tùy chọn / Bắt buộc → hiện chip
 * kèm ghi chú phân công tự động / lễ tân xếp.
 */
function computePreview(p: BookingPolicy): PreviewChip[] {
  const chips: PreviewChip[] = [];

  // Hình thức khám (#18673) — bước đầu khi hiển thị, phản ánh dimPaymentType thật thay vì cố định.
  if (p.dimPaymentType !== "HIDDEN") {
    chips.push({
      label: `Hình thức khám (${dimensionLabel(p.dimPaymentType)})`,
      tone: p.dimPaymentType === "REQUIRED" ? "required" : "optional",
    });
  }

  const dim = (
    label: string,
    mode: DimensionMode,
    fill?: FillMode,
    /** Ghi chú thắng fill — dùng cho phạm vi loại dịch vụ, thứ fill không nói được. */
    overrideNote?: string
  ): void => {
    if (mode === "HIDDEN") {
      // Bị ẩn khỏi bước của bệnh nhân — nhưng nếu có AUTO/MANUAL thì hệ thống vẫn xử lý.
      if (fill === "AUTO") {
        chips.push({ label, tone: "auto", note: "Ẩn · Tự động phân công" });
      } else if (fill === "MANUAL") {
        chips.push({ label, tone: "auto", note: "Ẩn · Lễ tân xếp" });
      }
      return;
    }
    const tone = mode === "REQUIRED" ? "required" : "optional";
    let note: string | undefined = overrideNote;
    if (!note && mode === "OPTIONAL") {
      if (fill === "AUTO") note = "Bỏ trống → Tự động phân công";
      else if (fill === "MANUAL") note = "Bỏ trống → Lễ tân xếp";
    }
    chips.push({
      label: `${label} (${dimensionLabel(mode)})`,
      tone,
      note,
    });
  };

  dim("Khoa", p.dimDepartment);
  dim("Phòng", p.dimRoom, p.roomFill);
  dim("Bác sĩ", p.dimDoctor, p.doctorFill);
  dim(
    "Dịch vụ",
    p.dimService,
    undefined,
    p.serviceScope === "EXAM_ONLY" ? "Chỉ dịch vụ khám bệnh" : undefined
  );

  // Ngày + giờ/buổi.
  chips.push({
    label: p.timeMode === "SLOT" ? `Ngày + Giờ (${p.slotMinutes}′)` : "Ngày + Buổi",
    tone: "required",
  });

  // STT.
  const scopeLabel =
    QUEUE_SCOPE_OPTIONS.find((s) => s.code === p.queueScope)?.label ?? p.queueScope;
  const resetLabel =
    QUEUE_RESET_OPTIONS.find((r) => r.code === p.queueReset)?.label ?? p.queueReset;
  chips.push({
    label: "STT",
    tone: "neutral",
    note: `${scopeLabel} · reset ${resetLabel.toLowerCase()}`,
  });

  return chips;
}

const DEFAULT_POLICY: BookingPolicy = {
  dimDepartment: "OPTIONAL",
  dimRoom: "HIDDEN",
  dimDoctor: "OPTIONAL",
  dimService: "OPTIONAL",
  dimPaymentType: "HIDDEN",
  serviceScope: "ALL",
  doctorFill: "AUTO",
  roomFill: "AUTO",
  timeMode: "SESSION",
  slotMinutes: 15,
  queueReset: "DAY",
  queueScope: "GENERAL",
};

const DIM_KEY_LABEL: Record<BookingDimensionKey, string> = {
  department: "Khoa",
  room: "Phòng",
  doctor: "Bác sĩ",
  service: "Dịch vụ",
};

/** Cài đặt · Đặt lịch — Booking Policy editor. Real /api/v1/booking-policy. */
export function BookingPolicySettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: bookingPolicyQueryKeys.all(),
    queryFn: () => bookingPolicyApi.get(),
  });

  /*
   * Danh mục dịch vụ, chỉ để đếm xem có dịch vụ loại "Khám bệnh" nào đang bật không.
   *
   * Cần vì ClinicService.serviceType mặc định là SERVICE: một phòng khám chưa từng khai loại cho
   * dịch vụ nào thì TẤT CẢ dịch vụ của họ là "Dịch vụ / thủ thuật", và "Chỉ dịch vụ khám bệnh" ở đó
   * làm trống ô chọn của bệnh nhân. Nói ra ở đây, lúc còn sửa được, thay vì để họ phát hiện trong
   * app. Dùng chung queryKey với màn Dịch vụ nên thường đã có cache.
   */
  const servicesQ = useQuery({
    queryKey: servicesQueryKeys.all(),
    queryFn: () => servicesApi.listAll(),
  });
  const activeExamCount = (servicesQ.data ?? []).filter(
    (s) => s.active && s.serviceType === "EXAM"
  ).length;

  const [policy, setPolicy] = useState<BookingPolicy>(DEFAULT_POLICY);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) setPolicy(q.data);
  }, [q.data]);

  const set = <K extends keyof BookingPolicy>(key: K, value: BookingPolicy[K]) => {
    setPolicy((prev) => ({ ...prev, [key]: value }));
    setMsg(null);
  };

  // Thứ tự 4 chiều — dùng dimOrder nếu hợp lệ (đủ 4 khoá), else thứ tự mặc định.
  const dimOrder: BookingDimensionKey[] =
    policy.dimOrder && policy.dimOrder.length === 4
      ? policy.dimOrder
      : DEFAULT_DIMENSION_ORDER;
  const moveDim = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= dimOrder.length) return;
    const next = [...dimOrder];
    [next[idx], next[j]] = [next[j], next[idx]];
    set("dimOrder", next);
  };

  const preview = useMemo(() => computePreview(policy), [policy]);

  const save = useMutation({
    mutationFn: () =>
      bookingPolicyApi.update({
        ...policy,
        slotMinutes: Math.max(1, policy.slotMinutes),
      }),
    onSuccess: (p) => {
      qc.setQueryData(bookingPolicyQueryKeys.all(), p);
      setPolicy(p);
      setMsg("Đã lưu cấu hình đặt lịch.");
      setError(null);
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
    },
  });

  const dimSelect = (
    label: string,
    key: "dimDepartment" | "dimRoom" | "dimDoctor" | "dimService" | "dimPaymentType",
    value: DimensionMode
  ) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={(v) => set(key, v as DimensionMode)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DIMENSION_OPTIONS.map((o) => (
            <SelectItem key={o.code} value={o.code}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const fillSelect = (
    label: string,
    key: "doctorFill" | "roomFill",
    value: FillMode
  ) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={(v) => set(key, v as FillMode)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FILL_OPTIONS.map((o) => (
            <SelectItem key={o.code} value={o.code}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Đặt lịch — Luồng & phân công"
        description={
          <>
            Cấu hình các bước bệnh nhân thấy khi đặt lịch (Khoa / Phòng / Bác sĩ /
            Dịch vụ) và cách phân công bác sĩ &amp; phòng. Xem trước luồng ở bên phải.
          </>
        }
      />

      {q.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
      {q.isError && (
        <p className="text-sm text-destructive">Không tải được cấu hình.</p>
      )}

      {!q.isLoading && (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
          {/* ── Cột trái: form ─────────────────────────────── */}
          <div className="grid gap-4">
            {/* Chiều đặt lịch */}
            <Card>
              <CardContent className="grid gap-3 pt-6">
                <h2 className="text-base font-semibold">Chiều đặt lịch</h2>
                <p className="text-sm text-muted-foreground">
                  Mỗi chiều có thể Ẩn, để Tùy chọn hoặc Bắt buộc bệnh nhân chọn.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {dimSelect("Khoa", "dimDepartment", policy.dimDepartment)}
                  {dimSelect("Phòng", "dimRoom", policy.dimRoom)}
                  {dimSelect("Bác sĩ", "dimDoctor", policy.dimDoctor)}
                  {dimSelect("Dịch vụ", "dimService", policy.dimService)}
                  {dimSelect("Hình thức khám (BHYT/Dịch vụ)", "dimPaymentType", policy.dimPaymentType)}
                </div>

                {/*
                  Chỉ hỏi khi bước "Dịch vụ" thực sự hiện. dimService = Ẩn thì phạm vi vô nghĩa, và
                  một ô cài đặt không có tác dụng gì là một ô gây nhầm.
                */}
                {policy.dimService !== "HIDDEN" && (
                  <div className="mt-2 space-y-1.5">
                    <Label>Loại dịch vụ bệnh nhân tự đặt được</Label>
                    <Select
                      value={policy.serviceScope}
                      onValueChange={(v) => set("serviceScope", v as ServiceScope)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_SCOPE_OPTIONS.map((o) => (
                          <SelectItem key={o.code} value={o.code}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      Thủ thuật, xét nghiệm và chẩn đoán hình ảnh là thứ bác sĩ chỉ định trong lúc
                      khám, và chúng không sinh phí khám để bệnh nhân trả trước — chọn{" "}
                      <strong>Chỉ dịch vụ khám bệnh</strong> để bỏ chúng khỏi ô chọn. Trung tâm chẩn
                      đoán, nơi bệnh nhân tự đặt siêu âm hay X-quang là đúng nghiệp vụ, giữ{" "}
                      <strong>Tất cả dịch vụ</strong>. Loại của từng dịch vụ khai ở{" "}
                      <Link className="underline" to="/settings/services">
                        Cài đặt · Dịch vụ
                      </Link>
                      .
                    </p>
                    {policy.serviceScope === "EXAM_ONLY" &&
                      servicesQ.isSuccess &&
                      activeExamCount === 0 && (
                        <p className="text-sm text-destructive">
                          Phòng khám chưa có dịch vụ nào loại <strong>Khám bệnh</strong> đang bật,
                          nên ô chọn dịch vụ của bệnh nhân sẽ TRỐNG. Khai loại cho ít nhất một dịch
                          vụ khám, hoặc đặt chiều Dịch vụ thành Ẩn để bệnh nhân đặt theo chuyên
                          khoa.
                        </p>
                      )}
                  </div>
                )}

                <div className="mt-2 space-y-1.5">
                  <Label>Thứ tự hiển thị các chiều</Label>
                  <p className="text-sm text-muted-foreground">
                    Áp dụng cho màn đặt lịch và màn khám (Hẹn tái khám). Dùng ↑/↓ để đổi thứ tự.
                  </p>
                  <ol className="grid list-none gap-1.5 p-0">
                    {dimOrder.map((k, i) => (
                      <li
                        key={k}
                        className="flex items-center justify-between rounded-md border px-3 py-2"
                      >
                        <span className="text-sm">
                          <span className="tabular-nums text-muted-foreground">{i + 1}.</span>{" "}
                          {DIM_KEY_LABEL[k]}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={i === 0}
                            aria-label={`Đưa ${DIM_KEY_LABEL[k]} lên`}
                            onClick={() => moveDim(i, -1)}
                          >
                            ↑
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={i === dimOrder.length - 1}
                            aria-label={`Đưa ${DIM_KEY_LABEL[k]} xuống`}
                            onClick={() => moveDim(i, 1)}
                          >
                            ↓
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Phân công */}
            <Card>
              <CardContent className="grid gap-3 pt-6">
                <h2 className="text-base font-semibold">Phân công</h2>
                <p className="text-sm text-muted-foreground">
                  Khi bệnh nhân bỏ trống: Tự động (hệ thống chọn), Lễ tân xếp (thủ công ở
                  quầy) hoặc Không.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {fillSelect("Xếp bác sĩ", "doctorFill", policy.doctorFill)}
                  {fillSelect("Xếp phòng", "roomFill", policy.roomFill)}
                </div>
              </CardContent>
            </Card>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {msg && <p className="text-sm text-emerald-600">{msg}</p>}

            <div className="flex justify-end">
              <Button
                type="button"
                disabled={save.isPending}
                onClick={() => {
                  setMsg(null);
                  setError(null);
                  save.mutate();
                }}
              >
                {save.isPending ? "Đang lưu…" : "Lưu thay đổi"}
              </Button>
            </div>
          </div>

          {/* ── Cột phải: Xem trước ────────────────────────── */}
          <Card className="lg:sticky lg:top-4">
            <CardContent className="grid gap-3 pt-6">
              <h2 className="text-base font-semibold">Xem trước luồng đặt lịch</h2>
              <p className="text-sm text-muted-foreground">
                Các bước bệnh nhân sẽ thấy khi đặt lịch:
              </p>
              <ol className="grid list-none gap-2 p-0">
                {preview.map((chip, i) => (
                  <li key={`${chip.label}-${i}`} className="flex items-start gap-2">
                    <span className="min-w-[18px] pt-1 text-xs tabular-nums text-muted-foreground">
                      {i + 1}.
                    </span>
                    <div className="grid gap-0.5">
                      <span>
                        <Badge variant={chipVariant(chip.tone)}>{chip.label}</Badge>
                      </span>
                      {chip.note && (
                        <span className="text-xs text-muted-foreground">{chip.note}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              <p className="text-xs text-muted-foreground">
                Chiều để "Ẩn" không hiện với bệnh nhân; nếu cấu hình Tự động / Lễ tân xếp
                thì hệ thống hoặc quầy sẽ hoàn tất sau.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function chipVariant(tone: PreviewChip["tone"]): BadgeProps["variant"] {
  switch (tone) {
    case "required":
      return "destructive";
    case "optional":
      return "secondary";
    case "auto":
      return "warning";
    default:
      return "muted";
  }
}
